import React, { useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button, TextField,
  Divider, Alert, IconButton, Tabs, Tab, CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';

function GoogleButton({ onClick, loading }) {
  return (
    <Button fullWidth variant="outlined" onClick={onClick} disabled={loading}
      startIcon={<Box component="img" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
        alt="Google" sx={{ width: 18, height: 18 }} />}
      sx={{
        color: '#e8f0f8', borderColor: 'rgba(245,166,35,0.4)', fontWeight: 600,
        py: 1.1, fontSize: '0.9rem', textTransform: 'none',
        '&:hover': { borderColor: '#2ea84b', backgroundColor: 'rgba(46,168,75,0.06)' },
      }}>
      Continue with Google
    </Button>
  );
}

const fieldSx = {
  '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(245,166,35,0.4)' },
  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: '#2ea84b' },
  '& label': { color: '#9eb8d0' },
};

function friendlyError(msg = '') {
  if (msg.includes('Invalid login credentials')) return 'Incorrect email or password.';
  if (msg.includes('Email not confirmed'))       return 'Please confirm your email before signing in.';
  if (msg.includes('already registered'))        return 'An account with that email already exists.';
  if (msg.includes('Password should be'))        return 'Password must be at least 6 characters.';
  if (msg.includes('rate limit'))                return 'Too many attempts. Try again in a minute.';
  if (msg.includes('network'))                   return 'Network error — check your connection.';
  return msg || 'Something went wrong. Please try again.';
}

export default function AuthModal() {
  const {
    authModalOpen, setAuthModalOpen, currentUser,
    // Lifted to context (not local state) so a caller can choose which
    // tab this singleton modal opens on — e.g. RequireAuth's invite-page
    // "New to Football for Nerds?" button opens straight to Create
    // Account instead of duplicating this form. Aliased to tab/setTab so
    // every other reference in this file is unchanged.
    authModalTab: tab, setAuthModalTab: setTab,
  } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(null);
  const [forgotMode, setForgotMode] = useState(false);
  // Set to the attempted email when signUp() indicates that email already
  // has an account — see handleEmailAuth for how that's detected. Distinct
  // from `error` so it renders its own "Sign In / Reset Password" panel
  // instead of a plain red error message.
  const [existingAccountEmail, setExistingAccountEmail] = useState(null);

  const reset = () => {
    setEmail(''); setPassword(''); setFirstName(''); setLastName('');
    setError(null); setLoading(false); setSuccess(null); setForgotMode(false);
    setExistingAccountEmail(null);
  };
  const close = () => { setAuthModalOpen(false); reset(); };

  // Invite pages always win: signing up or in from /join/:code must return
  // the guardian to that exact URL, never anywhere else — unchanged from
  // before. Off an invite page, a fresh Create Account (tab === 1) now
  // explicitly routes to the Getting Started launchpad instead of the
  // site root, using the SAME signal the modal already has (which tab is
  // active) rather than inferring "is this a new account" after the fact
  // from anything server-side. Sign In (tab 0) is untouched either way —
  // Google's redirect there is exactly what it was before this change.
  const isInviteRoute = () => window.location.pathname.startsWith('/join/');
  const googleRedirectUrl = () => {
    if (isInviteRoute()) return window.location.href;
    if (tab === 1) return `${window.location.origin}/getting-started`;
    return window.location.origin;
  };
  const signUpRedirectUrl = () => (isInviteRoute() ? window.location.href : `${window.location.origin}/getting-started`);

  const handleGoogle = async () => {
    setError(null); setLoading(true);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: googleRedirectUrl() },
    });
    if (err) { setError(friendlyError(err.message)); setLoading(false); }
    // On success, page redirects — no need to close
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault(); setError(null); setSuccess(null); setExistingAccountEmail(null); setLoading(true);
    try {
      if (tab === 0) {
        // Sign in
        const { error: err } = await supabase.auth.signInWithPassword({ email, password });
        if (err) throw err;
        close();
      } else {
        // Sign up — first/last name are required and become
        // raw_user_meta_data.first_name/.last_name, which the server-side
        // handle_new_user() trigger (010) reads to populate profiles and
        // derive display_name. No account-type/role choice here — that
        // stays a separate, later onboarding step (user_roles), not part
        // of account creation.
        if (!firstName.trim() || !lastName.trim()) {
          setError('First and last name are required.');
          setLoading(false);
          return;
        }
        const { data, error: err } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { first_name: firstName.trim(), last_name: lastName.trim() },
            // /join/:code's exact URL on an invite page (017); otherwise
            // /getting-started — this project requires email confirmation,
            // so this is what the confirmation link actually returns the
            // new user to, in either case.
            emailRedirectTo: signUpRedirectUrl(),
          },
        });

        // Supabase signals "this email already has an account" two
        // different ways depending on the project's email-confirmation
        // setting, and both are read only from THIS signUp() response —
        // nothing here independently queries profiles/auth.users:
        //   - confirmation OFF: signUp() returns a real error whose
        //     message includes "already registered".
        //   - confirmation ON: signUp() deliberately returns NO error at
        //     all (to avoid leaking which emails exist via an error
        //     message) — instead `data.user` comes back with an EMPTY
        //     identities array. That's Supabase's own documented signal
        //     for this exact case.
        const existingAccount =
          (err && /already registered/i.test(err.message)) ||
          (!err && data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0);

        if (existingAccount) {
          setExistingAccountEmail(email.trim());
          setLoading(false);
          return;
        }

        if (err) throw err;
        if (data?.session) {
          // Email confirmation isn't required (or was auto-confirmed) —
          // signUp() already returned a live session, so the user is
          // signed in right now. Showing a "check your email" message in
          // that case would be actively misleading.
          close();
          // Same explicit, tab-driven routing as signUpRedirectUrl() above
          // (used when a confirmation email is actually sent) — here the
          // session already exists, so this fires the navigation directly
          // instead of waiting on a redirect that isn't going to happen.
          // Never fires on an invite page — close() alone is correct there.
          if (!isInviteRoute()) navigate('/getting-started');
        } else {
          // Confirmation is required — no session yet. Give a clear,
          // honest state instead of guessing.
          setSuccess('Account created! Check your email to confirm, then sign in.');
          setLoading(false);
        }
      }
    } catch (err) {
      setError(friendlyError(err.message));
      setLoading(false);
    }
  };

  // "Sign In" action on the existing-account notice — hands the user
  // straight to the Sign In tab with their email carried over (same
  // `email` state, shared across tabs). The signup password they just
  // typed is deliberately not carried over as a guess at their real one.
  const handleExistingAccountSignIn = () => {
    setExistingAccountEmail(null);
    setPassword('');
    setFirstName('');
    setLastName('');
    setError(null);
    setSuccess(null);
    setTab(0);
  };

  // "Reset Password" action on the existing-account notice — reuses
  // handleForgotPassword() as-is (it already reads `email` from state,
  // already sends the same neutral response regardless of outcome). Not a
  // second, more specific message — see instructions on why neutral wording
  // stays neutral even here. Also switches into the same forgotMode UI the
  // "Forgot password?" link uses, so the request's brief in-flight window
  // shows that mini-form (with its own spinner) instead of flashing the
  // full Sign In form for a moment first.
  const handleExistingAccountReset = async () => {
    setExistingAccountEmail(null);
    setTab(0);
    setForgotMode(true);
    await handleForgotPassword();
  };

  // Neutral response regardless of whether the email is actually
  // registered — never reveal that via this flow. Requires an email to
  // already be typed into the Sign In form above; there's no separate
  // email field for this.
  const handleForgotPassword = async () => {
    setError(null); setSuccess(null);
    if (!email.trim()) {
      setError('Enter your email above, then click "Forgot password?" again.');
      return;
    }
    setLoading(true);
    try {
      await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
    } catch (_) {
      // Deliberately not surfaced — see neutral response note above.
    } finally {
      setLoading(false);
      setSuccess('If an account exists for that email, a password reset link is on its way.');
    }
  };

  const [newPassword, setNewPassword]       = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSuccess, setPwSuccess]             = useState(null);
  const [pwError, setPwError]                 = useState(null);
  const [pwLoading, setPwLoading]             = useState(false);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    close();
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError(null); setPwSuccess(null);
    if (newPassword !== confirmPassword) { setPwError('Passwords do not match.'); return; }
    if (newPassword.length < 6) { setPwError('Password must be at least 6 characters.'); return; }
    // Google OAuth users can't set a password this way
    const providers = currentUser?.app_metadata?.providers || [];
    if (providers.includes('google') && !providers.includes('email')) {
      setPwError('Your account uses Google sign-in. Password change is not available.');
      return;
    }
    setPwLoading(true);
    const timeout = setTimeout(() => {
      setPwLoading(false);
      setPwError('Request timed out. Please sign out, sign back in, and try again.');
    }, 8000);
    try {
      const { error: err } = await supabase.auth.updateUser({ password: newPassword });
      clearTimeout(timeout);
      if (err) setPwError(friendlyError(err.message));
      else { setPwSuccess('Password updated!'); setNewPassword(''); setConfirmPassword(''); }
    } catch (ex) {
      clearTimeout(timeout);
      setPwError('Session expired — please sign out and sign back in, then try again.');
    } finally {
      setPwLoading(false);
    }
  };

  // Already logged in — show account info
  if (currentUser) {
    return (
      <Dialog open={authModalOpen} onClose={close} maxWidth="xs" fullWidth
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.97)', border: '1px solid rgba(245,166,35,0.3)', borderRadius: 3 } }}>
        <DialogContent sx={{ p: 3 }}>
          <IconButton onClick={close} size="small" sx={{ position: 'absolute', top: 10, right: 10, color: '#9eb8d0' }}>✕</IconButton>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.25 }}>Account</Typography>
          <Typography variant="body2" sx={{ color: '#9eb8d0', mb: 2.5 }}>
            {currentUser.user_metadata?.display_name && (
              <><strong style={{ color: '#e8f0f8' }}>{currentUser.user_metadata.display_name}</strong><br /></>
            )}
            {currentUser.email}
          </Typography>

          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#f5a623', mb: 1, letterSpacing: '0.06em' }}>
            CHANGE PASSWORD
          </Typography>
          {pwError   && <Alert severity="error"   sx={{ mb: 1.5, fontSize: '0.78rem' }}>{pwError}</Alert>}
          {pwSuccess && <Alert severity="success" sx={{ mb: 1.5, fontSize: '0.78rem' }}>{pwSuccess}</Alert>}
          {/* Google OAuth users get a password reset email instead */}
          {(currentUser?.app_metadata?.providers || []).includes('google') &&
           !(currentUser?.app_metadata?.providers || []).includes('email') ? (
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ color: '#64748b', fontSize: '0.78rem', mb: 1.25, lineHeight: 1.5 }}>
                Your account uses Google sign-in. To set a password, we'll send a reset link to <strong style={{ color: '#9eb8d0' }}>{currentUser.email}</strong>.
              </Typography>
              <Button
                variant="outlined" fullWidth size="small"
                disabled={pwLoading || !!pwSuccess}
                onClick={async () => {
                  setPwLoading(true); setPwError(null); setPwSuccess(null);
                  try {
                    const { error: err } = await supabase.auth.resetPasswordForEmail(currentUser.email, {
                      redirectTo: `${window.location.origin}/reset-password`,
                    });
                    if (err) setPwError(err.message);
                    else setPwSuccess('Reset link sent! Check your email.');
                  } catch { setPwError('Something went wrong.'); }
                  finally { setPwLoading(false); }
                }}
                sx={{ borderColor: 'rgba(245,166,35,0.4)', color: '#f5a623', fontSize: '0.8rem', '&:hover': { borderColor: '#f5a623', bgcolor: 'rgba(245,166,35,0.06)' } }}>
                {pwLoading ? <CircularProgress size={16} sx={{ color: '#f5a623' }} /> : 'Send Password Reset Email →'}
              </Button>
            </Box>
          ) : (
          <Box component="form" onSubmit={handleChangePassword} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2.5 }}>
            <TextField label="New Password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              size="small" fullWidth required inputProps={{ minLength: 6 }} autoComplete="new-password" sx={fieldSx} />
            <TextField label="Confirm Password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              size="small" fullWidth required inputProps={{ minLength: 6 }} autoComplete="new-password" sx={fieldSx} />
            <Button type="submit" variant="contained" color="primary" fullWidth disabled={pwLoading}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              {pwLoading ? <CircularProgress size={18} color="inherit" /> : 'Update Password'}
            </Button>
          </Box>
          )}

          <Divider sx={{ borderColor: 'rgba(245,166,35,0.15)', mb: 2 }} />
          <Button fullWidth variant="outlined" color="error" onClick={handleSignOut}
            sx={{ textTransform: 'none', fontWeight: 600 }}>Sign Out</Button>
        </DialogContent>
      </Dialog>
    );
  }

  const isSignup = tab === 1;

  return (
    <Dialog open={authModalOpen} onClose={close} maxWidth="xs" fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(8,18,32,0.99)', border: '1px solid #2ea84b55',
          borderRadius: 3, backgroundImage: 'none',
          boxShadow: '0 0 80px rgba(46,168,75,0.12)',
        },
      }}>
      <DialogContent sx={{ p: 3, pt: 3.5, position: 'relative' }}>
        <IconButton onClick={close} size="small"
          sx={{ position: 'absolute', top: 10, right: 10, color: '#4a6080' }}>✕</IconButton>

        <Box sx={{ textAlign: 'center', mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
            🏈 <span style={{ color: '#2ea84b' }}>Football</span> for Nerds
          </Typography>
          <Typography variant="body2" sx={{ color: '#9eb8d0', mt: 0.5 }}>
            {isSignup ? 'Create your account' : 'Welcome back'}
          </Typography>
        </Box>

        <GoogleButton onClick={handleGoogle} loading={loading} />

        <Divider sx={{ my: 2, borderColor: 'rgba(245,166,35,0.25)' }}>
          <Typography variant="caption" sx={{ color: '#4a6080', px: 1 }}>or</Typography>
        </Divider>

        <Tabs value={tab} onChange={(_, v) => { setTab(v); setError(null); setSuccess(null); setForgotMode(false); setExistingAccountEmail(null); }} textColor="inherit"
          indicatorColor="primary" variant="fullWidth"
          sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', minHeight: 36, py: 0 } }}>
          <Tab label="Sign In" />
          <Tab label="Create Account" />
        </Tabs>

        {error   && <Alert severity="error"   sx={{ mb: 2, fontSize: '0.8rem' }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, fontSize: '0.8rem' }}>{success}</Alert>}

        {existingAccountEmail ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
              An account already exists for this email.
            </Alert>
            <Button variant="contained" color="primary" fullWidth onClick={handleExistingAccountSignIn}
              sx={{ py: 1.1, fontWeight: 700, textTransform: 'none', fontSize: '0.95rem' }}>
              Sign In
            </Button>
            <Button variant="outlined" fullWidth disabled={loading} onClick={handleExistingAccountReset}
              sx={{ py: 1.1, fontWeight: 700, textTransform: 'none', fontSize: '0.95rem', borderColor: 'rgba(245,166,35,0.4)', color: '#f5a623' }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Reset Password'}
            </Button>
          </Box>
        ) : !success && tab === 0 && forgotMode ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: '#9eb8d0' }}>
              Enter your email above (or below) and we'll send a link to reset your password.
            </Typography>
            <TextField label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)}
              size="small" fullWidth required autoComplete="email" sx={fieldSx} />
            <Button variant="contained" color="primary" fullWidth disabled={loading} onClick={handleForgotPassword}
              sx={{ mt: 0.5, py: 1.1, fontWeight: 700, textTransform: 'none', fontSize: '0.95rem' }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Send Reset Link →'}
            </Button>
            <Button onClick={() => { setForgotMode(false); setError(null); setSuccess(null); }}
              sx={{ textTransform: 'none', color: '#6a8aa8', alignSelf: 'center' }}>
              Back to Sign In
            </Button>
          </Box>
        ) : !success && (
          <Box component="form" onSubmit={handleEmailAuth} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {isSignup && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField label="First Name" value={firstName} onChange={e => setFirstName(e.target.value)}
                  size="small" fullWidth required autoComplete="given-name" sx={fieldSx} />
                <TextField label="Last Name" value={lastName} onChange={e => setLastName(e.target.value)}
                  size="small" fullWidth required autoComplete="family-name" sx={fieldSx} />
              </Box>
            )}
            <TextField label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)}
              size="small" fullWidth required autoComplete="email" sx={fieldSx} />
            <TextField label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)}
              size="small" fullWidth required autoComplete={tab === 0 ? 'current-password' : 'new-password'}
              inputProps={{ minLength: 6 }} sx={fieldSx} />
            {tab === 0 && (
              <Button onClick={() => { setForgotMode(true); setError(null); setSuccess(null); }}
                sx={{ textTransform: 'none', color: '#6a8aa8', fontSize: '0.78rem', alignSelf: 'flex-end', mt: -0.75, p: 0, minWidth: 0 }}>
                Forgot password?
              </Button>
            )}
            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading}
              sx={{ mt: 0.5, py: 1.1, fontWeight: 700, textTransform: 'none', fontSize: '0.95rem' }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : isSignup ? 'Create Account →' : 'Sign In →'}
            </Button>
          </Box>
        )}

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#3a5070', mt: 2, lineHeight: 1.5 }}>
          By continuing you agree to our Terms of Service and Privacy Policy.
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
